import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { PdfEmbedNodeView } from "./PdfEmbedNodeView";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    pdfEmbed: {
      setPdfEmbed: (options: {
        src: string;
        name?: string;
      }) => ReturnType;
    };
  }
}

export const PdfEmbed = Node.create({
  name: "pdfEmbed",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-src"),
        renderHTML: (attributes) => {
          if (!attributes.src) return {};
          return { "data-src": attributes.src };
        },
      },
      name: {
        default: "document.pdf",
        parseHTML: (element) => element.getAttribute("data-name"),
        renderHTML: (attributes) => {
          if (!attributes.name) return {};
          return { "data-name": attributes.name };
        },
      },
      isLoading: {
        default: false,
        parseHTML: (element) => element.getAttribute("data-loading") === "true",
        renderHTML: (attributes) =>
          attributes.isLoading ? { "data-loading": "true" } : {},
      },
      "data-placeholder-id": {
        default: null,
        parseHTML: (element) => element.getAttribute("data-placeholder-id"),
        renderHTML: (attributes) =>
          attributes["data-placeholder-id"]
            ? { "data-placeholder-id": attributes["data-placeholder-id"] }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="pdf-embed"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    // For SSR/static HTML, we render a simple container
    // The actual PDF rendering is handled by react-pdf in the NodeView
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "pdf-embed",
        class: "pdf-embed-wrapper",
      }),
      [
        "div",
        { class: "pdf-embed-placeholder" },
        `PDF: ${HTMLAttributes["data-name"] || "Document"}`,
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PdfEmbedNodeView);
  },

  addCommands() {
    return {
      setPdfEmbed:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});
